<template>
  <div id="app">
    <v-app class="chat" :style="{ color: defaultFontColor, fontFamily: fontFamily, fontSize: fontSize }">
      <v-img class="chat-background" v-if="bgImage" :src="bgImage" :class="{'show-img': isChatOpen === true}" />
      <template v-if="isChatOpen">
        <div @click="close" v-if="isEmbedded" class="header-chat-open" :style="{ backgroundColor: bgColor }">
          <img
            v-if="embeddedLogo"
            :src="embeddedLogo"
            alt=""
            :class="{
              'img-rect': embeddedLogoType === 'rectangular',
              'img-circular': embeddedLogoType === 'circular'
            }">
          <span>{{ title }}</span>

          <v-btn large icon class="header-chat-btn">
            <v-icon :style="{ color: defaultFontColor }">
              {{ mdiChevronDown }}
            </v-icon>
          </v-btn>
        </div>

        <TopBar 
          v-if="!isEmbedded" 
          class="header header-chat-open" 
        />

        <keep-alive>
          <ChatFlow class="main-content with-header" :class="{'pt-0' : !isHomeIcon}" />
        </keep-alive>

        <div class="footer">
          <ChatInput />
          <Branding />
        </div>

        <InstallPrompt v-if="isShowTooltip" />
        <NotificationsPrompt v-model="showNotificationPrompt" />
        <ConnectionProblem v-if="isConnectionProblem" />
        <InternetConnectionProblem v-if="isInternetConnectionProblem" />
        <LocationModal />
        <WebviewModal />
      </template>
      <template v-else>
        <button
          class="btn-open-chat"
          :class="{
            'button-rect': closeButtonType === 'rectangular',
            'button-circular': closeButtonType === 'circular'
          }"
          :style="{ backgroundColor: bgColor }"
          @click="open"
        >
          <img
            v-if="embeddedLogo"
            :src="embeddedLogo"
            alt=""
            :class="{
              'img-rect': embeddedLogoType === 'rectangular',
              'img-circular': embeddedLogoType === 'circular'
            }"
          >
          <span v-if="closeButtonType === 'rectangular'">{{ closeBtnText }}</span>
        </button>
      </template>
      <StartConversationPopup v-show="startConversation"/>
    </v-app>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mdiChevronDown, mdiHome, mdiShareVariant, mdiInformationOutline } from '@mdi/js';
import ChatFlow from './components/ChatFlow';
import ChatInput from './components/ChatInput';
import LocationModal from './components/LocationModal';
import WebviewModal from './components/WebviewModal';
import NotificationService from './services/notification.service';
import Branding from './components/Branding';
import InstallPrompt from './components/InstallPrompt';
import ConnectionProblem from './components/ConnectionProblem';
import InternetConnectionProblem from './components/InternetConnectionProblem';
import NotificationsPrompt from './components/NotificationsPrompt';
import TopBar from './components/TopBar';
import StartConversationPopup from './components/StartConversationPopup'

export default {
  name: "App",
  components: {
    ChatFlow,
    ChatInput,
    LocationModal,
    WebviewModal,
    Branding,
    InstallPrompt,
    ConnectionProblem,
    InternetConnectionProblem,
    NotificationsPrompt,
    TopBar,
    StartConversationPopup,
  },
  data() {
    return {
      chat: null,
      showNotificationPrompt: false,
      mdiChevronDown,
      mdiHome,
      mdiShareVariant,
      mdiInformationOutline,
      startConversation: true,

    };
  },
  async created() {
    await Promise.all([this.$store.dispatch('theme/getConfig'), this.$store.dispatch('chatFlow/init')]);
    this.$store.dispatch('theme/init');

    if (this.$store.getters['theme/isPushNotification'] && ('Notification' in window)) {
      const notificationService = new NotificationService();
      notificationService.init();

      if (Notification.permission !== 'granted' || Notification.isNotShownForSender) {
        this.showNotificationPrompt = true;
      }
    }

    window.addEventListener("load", () => {
      this.$store.commit('chatFlow/setInternetConnectionProblem', !navigator.onLine);
    });
    window.addEventListener("offline", () => {
      this.$store.commit('chatFlow/setInternetConnectionProblem', true);
    });
    window.addEventListener("online", () => {
      this.$store.commit('chatFlow/setInternetConnectionProblem', false);
    });
  },
  mounted: function() {
  //   window.addEventListener('beforeinstallprompt', (event) => {
  //     console.log("------------")

  //     // not show the default browser install app prompt
  //     event.preventDefault();

  //     // add the banner here or make it visible

  //     // save the event to use it later
  //     // (it has the important prompt method and userChoice property)
  //     window.promptEvent = event;
  //   });
    if(JSON.parse(sessionStorage.getItem('start-conversation'))){
      this.startConversation = false
    }
  },
  computed: {
    ...mapGetters({
      embeddedLogoType: 'theme/embeddedLogoType',
      isEmbedded: 'theme/isEmbedded',
      isChatOpen: 'theme/isOpen',
      embeddedLogo: 'theme/embeddedLogo',
      isConnectionProblem: 'chatFlow/isConnectionProblem',
      isInternetConnectionProblem: 'chatFlow/isInternetConnectionProblem',
      isShowTooltip: 'theme/isShowTooltip',
      isShowProfile: 'theme/isShowProfile'
    }),
    btnBgColor() {
      return this.$store.getters['theme/btnBgColor'];
    },
    brandName() {
      return this.$store.getters['theme/brandName'];
    },
    bgImage() {
      return this.$store.getters['theme/theme']?.content?.bgImage;
    },
    isHomeIcon() {
      return this.$store.getters['theme/brand']?.homeIcon;
    },
    bgColor() {
      return this.$store.getters['theme/theme']?.common?.bgColor;
    },
    title() {
      return this.$store.getters['theme/boxConfig'].open?.text;
    },
    closeBtnText() {
      return this.$store.getters['theme/boxConfig'].close?.text;
    },
    closeButtonType() {
      return this.$store.getters['theme/boxConfig'].close?.displayType;
    },
    defaultFontColor() {
      return this.$store.getters['theme/theme'].common?.fontColor;
    },
    fontFamily() {
      return this.$store.getters['theme/theme'].content?.fontFamily;
    },
    fontSize() {
      const size = this.$store.getters['theme/theme'].content?.fontSize;
      return size && `${size}px`;
    }
  },
  methods: {
    open() {
      this.$store.dispatch('theme/open');
    },
    close() {
      this.$store.dispatch('theme/close');
    },
  },
  beforeDestroy() {
    this.$store.dispatch('chatFlow/close');
  },
};
</script>

<style lang="scss" scoped>
.chat {
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  background-position: top center !important;
  background-attachment: fixed !important;
  position: relative;
  z-index: 1;
}

.chat-background {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  display: block;
  z-index: -1;

  &:not(.show-img) {
    visibility: hidden;
  }
}

.btn-open-chat {
  display: flex;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
  border-radius: $base-border-radius;
  padding: 0 $content-padding-x;
  font-weight: bold;

  img {
    margin-right: $content-padding-x;
  }
}

.header-chat-open {
  display: flex;
  align-items: center;
  // padding: 0.5em 1.2em;
  padding: 0.5em 1.4em;
  font-weight: bold;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 202 !important;
  height: 50px;

  img {
    margin: 0 10px 0 0;
    width: 50px;
    height: 50px;
    object-fit: cover;
    border-radius: 50%;
  }

  .header-chat-btn {
    margin-left: auto;
  }

  &.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 2;
  }
}

.main-content {
  width: 100%;
  overflow-x: hidden;
  padding-bottom: 6em;

  &.with-header {
    $header-size: 50px;

    padding-top: $header-size - 5px;
  }
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  // padding: $content-padding;
  padding: 0.5em 1.4em;
  padding-top: $input-padding-top;
  padding-bottom: 0;
  width: 100vw;
}

// .img-rect {
//   //
// }
.img-circular {
  border-radius: 50%;
}

// .button-rect {
//   //
// }
.button-circular {
  margin: 0 0 0 auto;
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

.mt {
  padding-top: 0 !important
}
</style>

<style lang="scss">
@import './styles/styles.scss';
</style>
